import styles from "./agenda.module.scss";
import { Tooltip } from "@mui/material";
import {
	getLegendDisplayOpacity,
	shouldDisplayLegendRule,
} from "helpers/legend-display";
import { LegendData } from "helpers/models";
import { useTranslation } from "react-i18next";

interface Props extends LegendData {
	onClose?: () => void;
}

interface LegendColor {
	raster: {
		color?: string;
		opacity?: number;
	};
	polygon: {
		fill?: string;
		fillOpacity?: number;
		stroke?: string;
		strokeDashArray?: any;
		strokeOpacity?: number;
	};
}

interface LegendItem {
	title: string;
	symbolizers: LegendColor[];
}

const BORDER_TITLES = new Set([
	"Outside",
	"No Data",
	"No_Data",
	"No Observation",
	"Negligible",
	"Negligible (< 0.014)",
	"Waterbody"
]);

const BORDER_COLORS = new Set(["#fff", "#ffffff", "#f2f2f2", "#dcdbdc", "grey", "gray"]);

const shouldShowLegendBorder = (
	title: string,
	backgroundColor: string | undefined,
	opacity: number | undefined
) => {
	if (opacity === 0 || BORDER_TITLES.has(title)) {
		return true;
	}

	const normalizedColor = String(backgroundColor || "").trim().toLowerCase();
	return BORDER_COLORS.has(normalizedColor);
};

function Agenda(props: Props) {
	const [t] = useTranslation();

	const handleStyleDisplay = (item: LegendItem) => {
		const opacity = item.symbolizers[0]?.raster?.opacity ?? item.symbolizers[0]?.polygon?.fillOpacity;
		return shouldDisplayLegendRule(props.layerId, item.title, opacity) ? "flex" : "none";
	}
	return (
		<div className={styles.agenda}>
			<div className={styles.header}>
				<Tooltip title={t("legend." + props.title)}>
					<div className={styles.text}>{t("legend." + props.title)}</div>
				</Tooltip>
				<div
					role="button"
					tabIndex={0}
					className={`material-icons ${styles.icon}`}
					onClick={props.onClose}
				>
					cancel
				</div>
			</div>
			<div>
				{props.subtitle && <div className={styles.subtitle}>{t("legend." + props.subtitle)}</div>}
				<div className={styles.legend}>
					{props.rules?.map((item: LegendItem, index: number) => (
						<div
							key={item.title + index}
							style={{
								display: handleStyleDisplay(item),
								alignItems: "center",
							}}
						>
							{(() => {
								let bg = item.symbolizers[0]?.raster?.color ?? item.symbolizers[0]?.polygon?.fill,
									opacity = item.symbolizers[0]?.raster?.opacity ?? item.symbolizers[0]?.polygon?.fillOpacity,
									border = shouldShowLegendBorder(item.title, bg, opacity)
										? "1px solid rgba(0, 0, 0, 0.1)"
										: "none"
								return <span
									style={{
										backgroundColor: bg,
										opacity: getLegendDisplayOpacity(props.layerId, item.title, opacity),
										border: border,
									}}
								></span>
							})()}
							{t("legend." + item.title)}
						</div>
					))}
				</div>
			</div>
		</div>
	);
}

export default Agenda;
